import { useState, useRef, useEffect } from "preact/hooks"
import { ChevronDown, Brain } from "lucide-react"
import { marked } from "marked"

interface ThinkingBlockProps {
  /** The thinking text content (may be streaming in) */
  content: string
  /** Whether thinking is still actively streaming */
  isStreaming?: boolean
  /** Whether the block should default to expanded */
  defaultExpanded?: boolean
  className?: string
}

/**
 * A collapsible thinking/reasoning block similar to Claude and ChatGPT.
 * Shows an animated indicator while thinking is active, then collapses
 * to show a summary. Can be expanded to see the full reasoning.
 */
export function ThinkingBlock({ content, isStreaming = false, defaultExpanded = false, className }: ThinkingBlockProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded || isStreaming)
  const contentRef = useRef<HTMLDivElement>(null)
  const prevStreamingRef = useRef(isStreaming)

  // Auto-expand while streaming, collapse when streaming finishes
  useEffect(() => {
    if (isStreaming) {
      setIsExpanded(true)
    } else if (prevStreamingRef.current && !isStreaming) {
      // Streaming just finished - collapse after a short delay
      const timer = setTimeout(() => setIsExpanded(false), 600)
      return () => clearTimeout(timer)
    }
    prevStreamingRef.current = isStreaming
  }, [isStreaming])

  // Auto-scroll thinking content while streaming
  useEffect(() => {
    if (isStreaming && isExpanded && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight
    }
  }, [content, isStreaming, isExpanded])

  if (!content) return null

  const thinkingDuration = computeThinkingDuration(content)
  const renderedHtml = marked.parse(content) as string

  return (
    <div class={`group ${className || ""}`}>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        class="flex items-center gap-2 py-1.5 text-sm transition-colors hover:text-gray-700 focus:outline-none"
      >
        {isStreaming ? (
          <ThinkingIndicator />
        ) : (
          <Brain className="w-3.5 h-3.5 text-gray-400" />
        )}
        <span class={`font-medium ${isStreaming ? "text-primary" : "text-gray-500"}`}>
          {isStreaming ? "Thinking..." : `Thought for ${thinkingDuration}`}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${
            isExpanded ? "" : "-rotate-90"
          }`}
        />
      </button>

      <div
        class={`overflow-hidden transition-all duration-300 ease-in-out ${
          isExpanded ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div
          ref={contentRef}
          class="ml-5.5 pl-3 border-l-2 border-gray-200 overflow-y-auto max-h-[380px] thinking-scroll"
        >
          <div
            class="thinking-content text-sm text-gray-500 leading-relaxed py-1"
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
          />
          {isStreaming && <ThinkingCursor />}
        </div>
      </div>
    </div>
  )
}

/** Animated thinking indicator - pulsing circles */
function ThinkingIndicator() {
  return (
    <div class="flex items-center gap-0.5">
      <div class="w-1.5 h-1.5 rounded-full bg-primary animate-thinking-1" />
      <div class="w-1.5 h-1.5 rounded-full bg-primary opacity-70 animate-thinking-2" />
      <div class="w-1.5 h-1.5 rounded-full bg-primary opacity-40 animate-thinking-3" />
    </div>
  )
}

/** A blinking cursor shown at the end of streaming thinking text */
function ThinkingCursor() {
  return (
    <span class="inline-block w-0.5 h-4 bg-primary animate-blink ml-0.5 align-text-bottom" />
  )
}

/** Estimate thinking duration from content length */
function computeThinkingDuration(content: string): string {
  // Rough estimate: ~4 tokens per word, ~75ms per token for thinking
  const words = content.split(/\s+/).length
  const seconds = Math.max(1, Math.round(words * 0.075))
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`
}

/** CSS for thinking block animations */
export function ThinkingBlockStyles() {
  return (
    <style>{`
      /* Thinking indicator pulse animation */
      @keyframes thinking-pulse-1 {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.4; transform: scale(0.75); }
      }
      @keyframes thinking-pulse-2 {
        0%, 100% { opacity: 0.7; transform: scale(0.85); }
        50% { opacity: 1; transform: scale(1); }
      }
      @keyframes thinking-pulse-3 {
        0%, 100% { opacity: 0.4; transform: scale(0.75); }
        50% { opacity: 0.7; transform: scale(0.85); }
      }
      .animate-thinking-1 { animation: thinking-pulse-1 1.4s ease-in-out infinite; }
      .animate-thinking-2 { animation: thinking-pulse-2 1.4s ease-in-out infinite 0.2s; }
      .animate-thinking-3 { animation: thinking-pulse-3 1.4s ease-in-out infinite 0.4s; }

      /* Blinking cursor */
      @keyframes blink {
        0%, 50% { opacity: 1; }
        51%, 100% { opacity: 0; }
      }
      .animate-blink { animation: blink 0.8s step-end infinite; }

      /* Thinking content styles */
      .thinking-content {
        line-height: 1.6;
      }
      .thinking-content > *:first-child {
        margin-top: 0;
      }
      .thinking-content > *:last-child {
        margin-bottom: 0;
      }
      .thinking-content p {
        margin-bottom: 0.5em;
      }
      .thinking-content ul, .thinking-content ol {
        margin-left: 1.25em;
        margin-bottom: 0.5em;
      }
      .thinking-content li {
        margin-bottom: 0.15em;
      }
      .thinking-content strong {
        font-weight: 600;
        color: #6b7280;
      }
      .thinking-content code {
        background-color: #f3f4f6;
        padding: 0.1em 0.25em;
        border-radius: 0.2em;
        font-size: 0.85em;
      }

      /* Custom scrollbar for thinking content */
      .thinking-scroll::-webkit-scrollbar {
        width: 4px;
      }
      .thinking-scroll::-webkit-scrollbar-track {
        background: transparent;
      }
      .thinking-scroll::-webkit-scrollbar-thumb {
        background-color: #d1d5db;
        border-radius: 2px;
      }
      .thinking-scroll::-webkit-scrollbar-thumb:hover {
        background-color: #9ca3af;
      }

      /* Margin-left using custom value for the border alignment */
      .ml-5\\.5 {
        margin-left: 1.375rem;
      }
    `}</style>
  )
}
